<template>
  <div
    :data-testid="dataTestidPrefix"
    class="notifications-table"
    :class="{ 'notifications-table--fill': fill }"
  >
    <lb-datatableaction
      ref="dataTableAction"
      :headers="headers"
      :tabledata="filterProjectionRows"
      :filter-options-data="filterProjectionRows"
      :displayed-row-count="displayedItems.length"
      :itemsperpage="itemsPerPage"
      :bulk-actions="false"
      :show-toolbar="showToolbar"
      :selectfilter="columnFilters"
      :auto-height="autoHeight"
      :data-testid="`${dataTestidPrefix}__datatable`"
      @filter-item="onFilterItem"
      @clear-item="onClearItem"
      @filter-active-change="$emit('filter-active-change', $event)"
    >
      <template v-slot:tbody>
        <tr
          v-for="(item, index) in tableRows"
          :key="item._id || index"
          :data-testid="`${dataTestidPrefix}__row--${index}`"
        >
          <template v-for="header in headers">
            <td
              v-if="header.visible && header.value === 'action'"
              :key="`action-${header.value}`"
              class="notifications-table__actions-cell"
              :data-testid="`${dataTestidPrefix}__cell--action--${index}`"
            >
              <div class="notifications-table__actions">
                <slot name="actions" :item="item" :index="index" />
              </div>
            </td>
            <td
              v-else-if="header.visible"
              :key="header.value"
              :data-testid="`${dataTestidPrefix}__cell--${header.value}--${index}`"
            >
              <slot :name="`cell-${header.value}`" :item="item" :index="index" :header="header">
                {{ item[header.value] }}
              </slot>
            </td>
          </template>
        </tr>
        <tr v-if="!displayedItems.length">
          <td :colspan="visibleHeaderCount" class="text-center pa-6">{{ emptyText }}</td>
        </tr>
      </template>
    </lb-datatableaction>
    <div v-if="showTablePagination" class="notifications-table__pager">
      <Pagination
        :key="paginationKey"
        :list="displayedItems"
        :itemsperpage="itemsPerPage"
        @paginated-data="onPaginatedData"
      />
    </div>
  </div>
</template>

<script>
import Pagination from '../../Pagination/Pagination.vue';

export default {
  name: 'NotificationsTable',
  components: { Pagination },
  props: {
    items: { type: Array, default: () => [] },
    sourceItems: { type: Array, default: null },
    headers: { type: Array, required: true },
    selectfilter: { type: Object, default: () => ({}) },
    showToolbar: { type: Boolean, default: false },
    itemsPerPage: { type: Number, default: 10 },
    autoHeight: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: '' },
    dataTestidPrefix: { type: String, default: 'notifications-table' },
    fill: { type: Boolean, default: false },
    /** Maps each row to flat header keys for column filter options and matching. */
    rowEnricher: { type: Function, default: null },
    /** Client-side footer pagination when filtered rows exceed itemsPerPage. */
    paginate: { type: Boolean, default: true },
  },
  data() {
    return {
      columnFilters: { ...(this.selectfilter || {}) },
      paginatedRows: [],
    };
  },
  computed: {
    resolvedSourceItems() {
      return this.sourceItems || this.items;
    },
    filterProjectionRows() {
      const rows = this.items || [];
      if (!this.rowEnricher) return rows;
      return rows.map((item) => this.rowEnricher(item));
    },
    displayedItems() {
      const rows = this.items || [];
      const filters = this.columnFilters;
      if (!filters || !Object.keys(filters).length) return rows;
      const filterFn = this.$nova && this.$nova.filterByDynamicCriteria;
      if (!filterFn) return rows;
      if (this.rowEnricher) {
        return rows.filter((item) => filterFn([this.rowEnricher(item)], filters).length > 0);
      }
      return filterFn(rows, filters);
    },
    visibleHeaderCount() {
      return (this.headers || []).filter((header) => header.visible !== false).length;
    },
    showTablePagination() {
      return this.paginate && this.displayedItems.length > this.itemsPerPage;
    },
    tableRows() {
      return this.showTablePagination ? this.paginatedRows : this.displayedItems;
    },
    paginationKey() {
      const filterKeys = Object.keys(this.columnFilters || {}).sort().join(',');
      return `${this.displayedItems.length}:${filterKeys}`;
    },
  },
  beforeDestroy() {
    this.columnFilters = {};
  },
  methods: {
    onFilterItem(filters) {
      this.columnFilters = filters && typeof filters === 'object' ? filters : {};
      this.$emit('filter-item', filters);
    },
    onClearItem() {
      this.columnFilters = {};
      this.$emit('clear-item');
    },
    clearFilters() {
      const table = this.$refs.dataTableAction;
      if (table && typeof table.clearfilter === 'function') {
        table.clearfilter();
        return;
      }
      this.columnFilters = {};
      this.$emit('clear-item');
    },
    onPaginatedData({ data }) {
      this.paginatedRows = Array.isArray(data) ? data : [];
    },
  },
};
</script>

<style lang="scss">
@import '../notificationLayout.scss';
</style>
